/* ==========================================================================
   MIMI 2026 - LA GRIGLIA DI COLLEZIONE RESTA SEMPRE PIENA
   ==========================================================================
   IL PROBLEMA
   Nelle pagine di collezione le celle si toccano e sono separate da un filetto
   (assets/mimi-griglia.css, modello buccellati.com). Quando il numero dei
   prodotti si divide male per il numero delle colonne, l'ultima riga resta
   monca e il disegno delle linee si interrompe a meta'.

   LA CURA
   Fra i prodotti si mettono delle TESSERE DI MOOD: fotografie d'atmosfera che
   prendono il posto di una cella. Le tessere arrivano da un serbatoio scritto
   in Liquid da snippets/mimi-serbatoio-mood.liquid:

     <template data-mimi-tessere>
       <div class="mimi-tessera" data-mimi-modello data-mimi-mood> ... </div>
       ...
     </template>

   Il serbatoio contiene, in quest'ordine: le fotografie del metacampo di
   collezione «mimi.mood», segnate con data-mimi-mood; i rimandi alle raccolte
   che seguono, cioe' l'apertura quadrata della raccolta di destinazione con
   l'invito «Scopri ...»; e per ultimo il segnaposto sobrio. Gli ultimi due
   strati sono la riserva: entrano in scena quando le fotografie di mood
   mancano, oppure quando i vuoti da riempire superano il loro numero.
   Le immagini dentro un <template> restano inerti: il browser le scarica
   soltanto quando una tessera viene copiata nella griglia. Alcune tessere
   sono collegamenti: la copia se li porta dietro e si comportano come
   qualunque altro collegamento della pagina.

   LE TESSERE HANNO DUE MESTIERI, E SI RICONOSCONO DAL SEGNO CHE PORTANO
     data-mimi-tessera="sparsa"  le fotografie di mood distribuite FRA i
                                 prodotti, una ogni quattro o cinque, dalla
                                 prima schermata in poi;
     data-mimi-tessera="coda"    quello che completa l'ultima riga rimasta
                                 monca, e sta soltanto in fondo.
   Il rimando alla raccolta che segue vive solo in coda: e' un invito a
   cambiare pagina, e ogni cinque prodotti diventerebbe un cartello
   pubblicitario. In mezzo ai prodotti vanno le fotografie e basta.

   IL PASSO, E PERCHE' NON E' SEMPRE LO STESSO
   Una tessera ogni quattro prodotti. Se pero' quella tessera cadesse nella
   stessa colonna della precedente si lascia passare un prodotto in piu' - il
   passo diventa cinque - e la colonna si sposta di una. Senza questo
   accorgimento, con un passo fisso e tre colonne le tessere si incolonnano e
   la griglia sembra sbagliata invece che composta.

   OGNI FOTOGRAFIA UNA VOLTA SOLA, POI L'ELENCO PULITO
   Le mood si consumano una volta e basta: la prima, la seconda, la terza, e
   quando sono finite le tessere smettono. Da li' in poi la griglia prosegue
   con i soli prodotti. Milano ne ha nove e le distribuisce nelle prime
   trentasei schede circa, Shanti tre, Vivienne due, Perle barocche e Perle
   South Sea una ciascuna: nessuna fotografia compare due volte.
   Il punto da cui si comincia si ricava da un'impronta dell'indirizzo della
   pagina e del numero di pagina, e da li' si gira l'elenco fino a esaurirlo.
   Cosi' pagine diverse aprono con fotografie diverse, ma nessuna pagina ne
   ripete una.
   La regola vale anche per la coda: quella puo' servirsi soltanto delle mood
   avanzate, e se le sparse hanno gia' consumato l'elenco passa al rimando
   «Scopri ...». Una fotografia in cima e di nuovo in fondo alla stessa pagina
   sarebbe una ripetizione come le altre.
   L'impronta NON guarda quanti prodotti si vedono: con lo scorrimento continuo
   quel numero cresce, e il disegno cambierebbe sotto gli occhi di chi legge.
   Il caso puro e' escluso per la stessa ragione: la stessa pagina deve
   mostrare sempre le stesse fotografie, altrimenti accorgersi di una
   fotografia sbagliata diventa impossibile.

   PERCHE' LE TESSERE GIA' A VIDEO NON SI SPOSTANO
   Il disegno (vedi «disegno» qui sotto) e' una funzione pura che va avanti in
   un senso solo: il disegno dei primi N prodotti e' sempre il principio del
   disegno di N piu' altri. Quando lo scorrimento continuo porta i prodotti
   nuovi, le tessere gia' messe cadono ancora dove il disegno dice, quindi si
   tengono e si aggiungono soltanto quelle nuove. Se invece una sola e' fuori
   posto - sono cambiate le colonne, o un filtro ha cambiato i prodotti - si
   rifa' tutto da capo, che e' l'unica cosa onesta da fare.
   E' la stessa proprieta' che impedisce all'elenco delle mood di ricominciare
   mentre si scorre: il conto delle tessere gia' messe si porta avanti da una
   passata all'altra invece di ripartire da zero, e le tappe sono al massimo
   quante sono le fotografie. Se su Milano le nove finiscono nella prima
   schermata, scorrendo non ne compaiono altre.

   PERCHE' IL CONTO SI FA QUI E NON IN LIQUID
   Le colonne cambiano con la larghezza dello schermo (2 da telefono, 3 da
   computer, e altre soglie se un domani si cambia impostazione) e il numero
   dei prodotti a video cambia con i filtri di Search & Discovery. Il conto
   fatto qui legge le colonne vere dal foglio di stile calcolato e i prodotti
   veri dal documento, quindi resta esatto in ogni combinazione.

   COME SI CONTANO LE COLONNE
   assets/theme.css costruisce la griglia con
     grid: auto-flow dense / repeat(auto-fit, var(--product-list-card-width));
   Il valore calcolato di grid-template-columns e' quindi l'elenco delle piste
   vere, una voce per colonna. La larghezza di una pista serve anche a capire
   quante colonne occupa una cella larga il doppio, come i riquadri
   promozionali del tema.

   NIENTE GIRI A VUOTO
   Ad ogni passata il conto viene confrontato con le tessere gia' presenti: se
   coincidono, il documento resta fermo. E' questa la ragione per cui gli
   osservatori qui sotto possono sorvegliare tutto il documento: le nostre
   stesse modifiche li risvegliano una volta sola, la passata successiva trova
   il conto gia' pareggiato e si ferma.
   ========================================================================== */

(() => {
  'use strict';

  const GRIGLIE = 'product-list.product-list:not(.product-list--carousel)';
  const SEGNO = 'data-mimi-tessera';
  const MODELLO = '[data-mimi-modello]';
  const MOOD = 'data-mimi-mood';
  const SERBATOIO = 'template[data-mimi-tessere]';

  /* Il passo fra una fotografia di mood e l'altra. */
  const PASSO_MINIMO = 4;

  let attesa = null;

  /* Legge un elenco di misure CSS e restituisce i numeri. */
  const misure = (valore) =>
    String(valore || '')
      .trim()
      .split(/\s+/)
      .map(parseFloat)
      .filter((n) => Number.isFinite(n));

  /* Un numero stabile ricavato da una stringa: le stesse lettere danno sempre
     lo stesso numero. Serve a scegliere da che punto dell'elenco delle mood
     cominciare, senza affidarsi al caso. */
  const impronta = (testo) => {
    let somma = 0;
    for (let i = 0; i < testo.length; i += 1) {
      somma = (somma * 31 + testo.charCodeAt(i)) % 1000003;
    }
    return somma;
  };

  /* Il seme della rotazione: l'indirizzo della pagina e il numero di pagina.
     Cambia da una raccolta all'altra e da una pagina all'altra, e resta lo
     stesso mentre si scorre e a ogni ricaricamento. */
  const semeDi = () => {
    let pagina = '1';
    try {
      pagina = new URLSearchParams(window.location.search).get('page') || '1';
    } catch (errore) {
      pagina = '1';
    }
    return `${window.location.pathname}|${pagina}`;
  };

  /* IL DISEGNO DELLE TESSERE SPARSE
     Dice, per una griglia di «quantiProdotti» prodotti su «colonne» colonne,
     dopo quale prodotto va messa ciascuna tessera e quale fotografia le tocca.

     Va avanti in un senso solo e non guarda mai indietro: e' questa proprieta'
     che tiene ferme le tessere gia' a video quando arrivano i prodotti della
     pagina successiva. */
  const disegno = (quantiProdotti, colonne, quanteMood, partenza) => {
    const tappe = [];
    if (quanteMood === 0) return tappe;

    /* Le tessere sono tante quante le fotografie, e non una di piu': l'elenco
       si consuma una volta sola e poi la griglia prosegue pulita. E' anche la
       ragione per cui lo scorrimento continuo non fa ricominciare il giro:
       arrivati a questo numero non nascono altre tappe, per quanti prodotti
       arrivino ancora. */
    const massimo = quanteMood;

    let celleEmesse = 0;
    let daUltima = 0;
    let colonnaPrecedente = -1;

    /* La prima tessera parte da quattro o da cinque a seconda della pagina,
       cosi' raccolte diverse non cominciano tutte allo stesso punto. */
    const primoPasso = PASSO_MINIMO + (partenza % 2);

    for (let n = 1; n <= quantiProdotti; n += 1) {
      celleEmesse += 1;
      daUltima += 1;

      if (tappe.length >= massimo) continue;

      const passoDovuto = tappe.length === 0 ? primoPasso : PASSO_MINIMO;
      if (daUltima < passoDovuto) continue;

      /* La colonna in cui cadrebbe la tessera: le celle finora emesse contate
         a partire da zero. Se e' la stessa dell'ultima tessera si lascia
         passare un prodotto in piu' - il passo diventa cinque - e la colonna
         si sposta di una. Con una colonna sola la domanda non ha senso e la
         verifica si salta del tutto, altrimenti il passo resterebbe per
         sempre a cinque. */
      let colonna = -1;
      if (colonne > 1) {
        colonna = celleEmesse % colonne;
        if (daUltima === passoDovuto && colonna === colonnaPrecedente) continue;
      }

      /* Si gira l'elenco a partire dal punto dato dall'impronta. Le tappe sono
         al massimo «quanteMood», quindi ogni fotografia esce una volta sola. */
      tappe.push({ dopo: n, foto: (partenza + tappe.length) % quanteMood });
      celleEmesse += 1;
      colonnaPrecedente = colonna;
      daUltima = 0;
    }

    return tappe;
  };

  /* Il serbatoio si cerca prima vicino alla griglia e poi in tutto il
     documento: oggi snippets/mimi-serbatoio-mood.liquid lo stampa una volta
     sola in fondo al corpo, e questa scala lascia aperta la porta a un
     serbatoio per sezione, se un domani servisse. */
  const serbatoioDi = (griglia) => {
    const ambiti = [
      griglia.closest('.collection__main'),
      griglia.closest('.shopify-section'),
      document,
    ];
    for (const ambito of ambiti) {
      if (ambito == null) continue;
      const trovato = ambito.querySelector(SERBATOIO);
      if (trovato != null) return trovato;
    }
    return null;
  };

  const tessereDi = (griglia) => Array.from(griglia.querySelectorAll(`[${SEGNO}]`));
  const codeDi = (griglia) => Array.from(griglia.querySelectorAll(`[${SEGNO}="coda"]`));

  /* Prepara la copia di un modello del serbatoio, pronta da mettere fra le
     celle: perde il segno del modello e prende quello del suo mestiere. */
  const copiaDi = (modello, mestiere) => {
    const copia = modello.cloneNode(true);
    copia.removeAttribute('data-mimi-modello');
    copia.setAttribute(SEGNO, mestiere);
    return copia;
  };

  /* La firma dice, in poche cifre, se per questa griglia e' cambiato qualcosa
     che riguarda il conto: quante celle ci sono, quanto e' larga la griglia e
     con quale disposizione. Sul sito lavorano diverse applicazioni che toccano
     il documento in continuazione; con la firma le loro modifiche costano una
     misura sola invece di una passata intera. */
  const memoria = new WeakMap();

  const firmaDi = (griglia, larghezza) =>
    [
      griglia.children.length,
      Math.round(larghezza),
      griglia.getAttribute('collection-desktop-layout') || '',
      griglia.getAttribute('collection-mobile-layout') || '',
    ].join('|');

  function completa(griglia) {
    const larghezzaGriglia = griglia.getBoundingClientRect().width;
    const firma = firmaDi(griglia, larghezzaGriglia);
    if (memoria.get(griglia) === firma) return;
    memoria.set(griglia, firma);

    const serbatoio = serbatoioDi(griglia);
    if (serbatoio == null) return;

    const modelli = Array.from(serbatoio.content.querySelectorAll(MODELLO));
    if (modelli.length === 0) return;

    /* Le fotografie di mood da una parte, la riserva dall'altra: la riserva
       sono i rimandi e il segnaposto, che restano nell'ordine in cui il
       serbatoio li scrive perche' quell'ordine e' una scala di preferenza. */
    const mood = modelli.filter((modello) => modello.hasAttribute(MOOD));
    const riserve = modelli.filter((modello) => !modello.hasAttribute(MOOD));

    const stile = getComputedStyle(griglia);
    if (stile.display.indexOf('grid') === -1) return;

    const piste = misure(stile.gridTemplateColumns);
    const passo = Math.max.apply(null, piste.concat([0]));
    if (passo <= 0) return;

    const spazio = parseFloat(stile.columnGap) || 0;

    /* Le colonne si contano in due modi e si tiene il numero piu' alto.
       L'elenco delle piste e' la via diretta; la larghezza della griglia
       divisa per il passo di una pista e' la controprova, e serve perche'
       repeat(auto-fit, ...) fa collassare a zero le piste rimaste vuote. */
    const perFila = Math.round((larghezzaGriglia + spazio) / (passo + spazio));
    const colonne = Math.max(piste.length, perFila);
    if (colonne < 1) return;

    /* LE TESSERE DI CODA SI RIFANNO SEMPRE, E PER PRIME.
       Lo scorrimento continuo appende i prodotti nuovi in fondo alla griglia,
       cioe' DOPO le tessere di coda gia' messe: lasciandole si ritroverebbero
       in mezzo ai prodotti. Sono al massimo due, toglierle costa nulla.
       Questo e' anche il punto in cui il selettore di vista da telefono
       romperebbe la griglia: passando a una colonna sola non c'e' piu' nessuna
       riga da completare, e la coda della disposizione precedente resterebbe
       li', larga tutta la pista. */
    codeDi(griglia).forEach((coda) => coda.remove());

    const celle = Array.from(griglia.children).filter((c) => c.tagName !== 'TEMPLATE');
    const prodotti = celle.filter((cella) => !cella.hasAttribute(SEGNO));
    if (prodotti.length === 0) return;

    const partenza = mood.length > 0 ? impronta(semeDi()) % mood.length : 0;
    const tappe = disegno(prodotti.length, colonne, mood.length, partenza);

    /* Le tessere sparse gia' a video si tengono se cadono dove il disegno
       dice; alla prima fuori posto si rifa' tutto da capo. */
    const sparse = [];
    let contoProdotti = 0;
    let intatte = true;

    for (const cella of celle) {
      if (!cella.hasAttribute(SEGNO)) {
        contoProdotti += 1;
        continue;
      }
      const tappa = tappe[sparse.length];
      if (tappa == null || tappa.dopo !== contoProdotti) {
        intatte = false;
        break;
      }
      sparse.push(cella);
    }

    if (!intatte) {
      tessereDi(griglia).forEach((tessera) => tessera.remove());
      sparse.length = 0;
    }

    /* Si mettono soltanto le tessere che mancano, ciascuna subito dopo il suo
       prodotto: quelle gia' a video non si toccano. */
    for (let i = sparse.length; i < tappe.length; i += 1) {
      const ancora = prodotti[tappe[i].dopo - 1];
      if (ancora == null) break;
      ancora.after(copiaDi(mood[tappe[i].foto], 'sparsa'));
    }

    /* Quante colonne occupano oggi le celle. Le tessere sparse valgono una
       colonna ciascuna, esattamente come una scheda prodotto, quindi entrano
       nel conto: e' il conto della coda che deve tenerne conto, altrimenti
       l'ultima riga tornerebbe spaiata. */
    let occupate = 0;
    for (const cella of Array.from(griglia.children)) {
      if (cella.tagName === 'TEMPLATE') continue;
      const larghezza = cella.getBoundingClientRect().width;
      if (larghezza <= 0) continue; /* celle spente dal foglio di stile */
      occupate += Math.max(1, Math.round((larghezza + spazio) / (passo + spazio)));
    }

    /* Con una colonna sola nessuna riga puo' restare monca: le fotografie
       sparse hanno senso lo stesso e restano, la coda no. */
    if (colonne >= 2 && occupate > 0) {
      const mancanti = (colonne - (occupate % colonne)) % colonne;

      if (mancanti > 0) {
        /* La fila della coda. Vale anche qui la regola: ogni fotografia una
           volta sola in pagina. La coda puo' quindi servirsi soltanto delle
           mood AVANZATE, cioe' quelle che le sparse non hanno ancora usato -
           il caso delle raccolte lunghe con poche fotografie, dove le sparse
           finiscono presto. Esaurito l'elenco si passa alla riserva: il
           rimando «Scopri ...» e, se manca anche quello, il segnaposto.
           Che la tessera stia in coda invece che sparsa non cambia niente per
           chi guarda: una fotografia in cima e di nuovo in fondo alla stessa
           pagina resta una ripetizione. */
        const avanzate = Math.max(0, mood.length - tappe.length);
        const fila = [];
        for (let i = 0; i < avanzate; i += 1) {
          fila.push(mood[(partenza + tappe.length + i) % mood.length]);
        }
        for (const riserva of riserve) fila.push(riserva);

        if (fila.length > 0) {
          const foglio = document.createDocumentFragment();
          for (let i = 0; i < mancanti; i += 1) {
            foglio.appendChild(copiaDi(fila[i % fila.length], 'coda'));
          }
          griglia.appendChild(foglio);
        }
      }
    }

    /* Il conto delle celle e' appena cambiato: si aggiorna la firma, cosi' la
       passata che il nostro stesso ritocco fa partire si ferma subito. */
    memoria.set(griglia, firmaDi(griglia, larghezzaGriglia));
  }

  function passa() {
    document.querySelectorAll(GRIGLIE).forEach(completa);
  }

  /* Una sola passata per fotogramma, qualunque sia il numero di richiami. */
  function programma() {
    if (attesa !== null) return;
    attesa = requestAnimationFrame(() => {
      attesa = null;
      passa();
    });
  }

  /* Il documento cambia quando i filtri ricaricano la sezione, quando si passa
     a una pagina successiva e quando l'editor del tema ridisegna la sezione. */
  if ('MutationObserver' in window) {
    new MutationObserver(programma).observe(document.documentElement, {
      childList: true,
      subtree: true,
      /* Il selettore di vista da telefono non aggiunge e non toglie celle:
         cambia soltanto l'attributo della disposizione sulla griglia. Senza
         ascoltarlo il conto non verrebbe rifatto, e le tessere resterebbero
         quelle della disposizione precedente. La firma legge gia' i due
         attributi, quindi questo non aggiunge giri a vuoto. */
      attributes: true,
      attributeFilter: ['collection-mobile-layout', 'collection-desktop-layout'],
    });
  }

  /* Le colonne cambiano con la larghezza della finestra e con l'apertura della
     colonna dei filtri. */
  if ('ResizeObserver' in window) {
    new ResizeObserver(programma).observe(document.documentElement);
  } else {
    window.addEventListener('resize', programma, { passive: true });
  }

  document.addEventListener('shopify:section:load', programma);
  window.addEventListener('load', programma);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', programma);
  } else {
    programma();
  }
})();
